/**
 * Shared date formatting utilities
 * All functions use en-US locale for consistency
 */

const LOCALE = 'en-US'

/**
 * Short date without year: "Jan 29"
 */
export function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric' })
}

/**
 * Standard date with year: "Jan 29, 2026"
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Long date with year: "January 29, 2026"
 */
export function formatLongDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(LOCALE, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Full date with weekday: "Wednesday, January 29"
 */
export function formatFullDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(LOCALE, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Short date with weekday: "Wed, Jan 29"
 */
export function formatDateWithWeekday(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(LOCALE, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Date with time: "Jan 29, 2026, 2:30 PM"
 */
export function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * 12-hour time: "2:30 PM"
 */
export function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':')
  const hour = parseInt(hours, 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

/**
 * Relative time: "Just now", "5m ago", "2h ago", "3d ago"
 * Falls back to short date after 7 days
 */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  return formatShortDate(dateStr)
}

/**
 * Days until a future date (negative if in past)
 */
export function getDaysUntil(dateStr: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(dateStr)
  target.setHours(0, 0, 0, 0)
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

/**
 * Get full month name from 0-indexed month number
 * @param month - 0-indexed month (0 = January, 11 = December)
 */
export function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return months[month]
}
